import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Settings as SettingsIcon, Globe } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import i18n from '../i18n';
import { useAuth } from '../contexts/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';

const languages = [
  { code: 'fr', name: 'Français', flagCode: 'FR' },
  { code: 'en', name: 'English', flagCode: 'GB' },
  { code: 'nl', name: 'Nederlands', flagCode: 'NL' },
  { code: 'de', name: 'Deutsch', flagCode: 'DE' },
  { code: 'it', name: 'Italiano', flagCode: 'IT' },
  { code: 'es', name: 'Español', flagCode: 'ES' },
];

const Settings = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const availableTabs = useMemo(() => {
    return [
      { key: 'general', label: t('tabGeneral') },
      { key: 'security', label: t('tabSecurity') },
    ];
  }, [t, i18n.language]);

  const [activeTab, setActiveTab] = useState('general');
  const tabCount = availableTabs.length;
  const tabsGridClass = tabCount === 3 ? 'grid-cols-3' : tabCount === 2 ? 'grid-cols-2' : 'grid-cols-1';

  const handleLanguageChange = (languageCode) => {
    setSelectedLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    localStorage.setItem('language', languageCode);
  };

  const handlePasswordSave = async () => {
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('passwordRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('passwordMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(t('passwordTooShort'));
      return;
    }
    setPasswordSaving(true);
    try {
      await axiosInstance.post('/api/users/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: t('passwordUpdated') });
    } catch (error) {
      setPasswordError(t('passwordSaveError'));
      toast({ title: t('passwordSaveError'), variant: 'destructive' });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Layout>
      <PageHeader title={
        <div className="flex items-center gap-2">
          <SettingsIcon className="h-8 w-8 text-muted-foreground" />
          <h1 className="text-3xl font-bold">{t('title')}</h1>
        </div>
      } />
      <div className="space-y-6">
        <div className="mb-6">
          <p className="text-muted-foreground text-sm">
            {t('subtitle')}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} key={i18n.language}>
          <TabsList className={`w-full grid ${tabsGridClass}`}>
            {availableTabs.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key} className="w-full">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="general" className="mt-4">
            <div className="border rounded-lg p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-xl font-semibold">{t('language')}</h2>
              </div>
              
              <p className="text-sm text-muted-foreground">
                {t('selectLanguage')}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                      selectedLanguage === lang.code
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <ReactCountryFlag
                      countryCode={lang.flagCode}
                      svg
                      style={{
                        width: '2em',
                        height: '2em',
                        objectFit: 'cover',
                        borderRadius: '0.25rem',
                      }}
                      title={lang.name}
                    />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{lang.name}</div>
                      <div className="text-xs text-muted-foreground">{lang.code.toUpperCase()}</div>
                    </div>
                    {selectedLanguage === lang.code && (
                      <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                        <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-4">
            <div className="border rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold">{t('securityTitle')}</h2>
              <p className="text-sm text-muted-foreground">{t('securityDescription')}</p>
              <div className="grid gap-4 max-w-xl">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('currentPassword')}</label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('currentPasswordPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('newPassword')}</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('newPasswordPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('confirmPassword')}</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('confirmPasswordPlaceholder')}
                  />
                </div>
                {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                <Button type="button" onClick={handlePasswordSave} disabled={passwordSaving}>
                  {passwordSaving ? t('passwordSaving') : t('passwordSave')}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Settings;